// components/Header.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useMobileDetection } from '../hooks/useMobileDetection';
import TransitionLink from './TransitionLink';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';
import '../index.css';



const Header = ({ isAboutDark }) => {
    const { isMobile } = useMobileDetection();
    const { language } = useLanguage();
    const t = useTranslation(language);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isHeroVisible, setIsHeroVisible] = useState(true);
    const [isOverWhiteSection, setIsOverWhiteSection] = useState(false);
    const location = useLocation();

    const isAboutPage = location.pathname === '/about';
    const isWorksPage = location.pathname === '/works';
    const isWorkDetailPage = location.pathname.includes('/work/');
    const isCodePage = location.pathname === '/code';
    const isCodeDetailPage = location.pathname.includes('/code/') && location.pathname !== '/code';
    const isPlaygroundPage = location.pathname === '/playground';
    const isArticlesPage = location.pathname === '/articles' || location.pathname.startsWith('/articles/');
    const isHomePage = location.pathname === '/';
    const [isOverBlackSection, setIsOverBlackSection] = useState(false);

    const isDarkPage = (isAboutDark || isAboutPage || isWorksPage || isCodePage || isCodeDetailPage || isPlaygroundPage || isArticlesPage || (isWorkDetailPage && !isOverWhiteSection));

    useEffect(() => {
        if (isAboutPage || isWorksPage || isCodePage || isCodeDetailPage || isPlaygroundPage || isArticlesPage) {
            setIsHeroVisible(false);
            setIsScrolled(true);
            return;
        }

        if (isWorkDetailPage) {
            setIsHeroVisible(false);
            setIsScrolled(true);

            const handleWorkDetailScroll = () => {
                const whiteSection = document.querySelector('.bg-white');
                if (whiteSection) {
                    const whiteSectionRect = whiteSection.getBoundingClientRect();
                    const headerHeight = 80;

                    setIsOverWhiteSection(
                        whiteSectionRect.top <= headerHeight &&
                        whiteSectionRect.bottom >= 0
                    );
                }
            };

            window.addEventListener('scroll', handleWorkDetailScroll);
            handleWorkDetailScroll();

            return () => window.removeEventListener('scroll', handleWorkDetailScroll);
        }

        const heroSection = document.querySelector('.hero-section');
        const aboutSection = document.querySelector('#about');

        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);

            if (heroSection) {
                const heroRect = heroSection.getBoundingClientRect();
                setIsHeroVisible(heroRect.bottom > 0);
            }

            if (aboutSection) {
                const aboutRect = aboutSection.getBoundingClientRect();
                const isAboutSectionVisible = aboutRect.top < 100 && aboutRect.bottom > 0;
                setIsScrolled(isAboutSectionVisible);
            }

            // Check if header is over black sections (CodeShowcase or ComputersShowcase)
            const codeShowcase = document.querySelector('.code-showcase-section');
            const computersShowcase = document.querySelector('.computers-showcase-section');
            const headerHeight = 80;

            let isOverBlack = false;

            if (codeShowcase) {
                const codeRect = codeShowcase.getBoundingClientRect();
                if (codeRect.top <= headerHeight && codeRect.bottom >= 0) {
                    isOverBlack = true;
                }
            }

            if (computersShowcase) {
                const computersRect = computersShowcase.getBoundingClientRect();
                if (computersRect.top <= headerHeight && computersRect.bottom >= 0) {
                    isOverBlack = true;
                }
            }

            setIsOverBlackSection(isOverBlack);
            setIsOverWhiteSection(!isOverBlack);
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, [isAboutPage, isWorksPage, isWorkDetailPage, isCodePage, isCodeDetailPage, isPlaygroundPage, isArticlesPage]);

    const getTextColor = () => {
        if (isWorkDetailPage && isOverWhiteSection) {
            return 'text-black';
        }

        if (isDarkPage) {
            return 'text-white';
        }

        // On homepage, if over black section (CodeShowcase or ComputersShowcase)
        if (isHomePage && isOverBlackSection) {
            return 'text-white';
        }

        if (isScrolled || !isHeroVisible) {
            return 'text-black';
        }

        return 'text-white';
    };

    const getLogoAccentColor = () => {
        if (isWorkDetailPage && isOverWhiteSection) {
            return 'text-gray-700';
        }

        if (isDarkPage) {
            return 'text-white';
        }

        // On homepage, if over black section
        if (isHomePage && isOverBlackSection) {
            return 'text-white';
        }

        return 'text-gray-700';
    };

    const textColorClass = getTextColor();
    const logoAccentColor = getLogoAccentColor();

    // Handle logo click - scroll to top if on homepage, navigate to home if on other pages
    const handleLogoClick = (e) => {
        if (isHomePage) {
            e.preventDefault();

            // Check if Lenis is available (desktop) or use native scroll (mobile)
            if (window.lenis && !isMobile) {
                window.lenis.scrollTo(0, {
                    duration: 1.5,
                    easing: (t) => 1 - Math.pow(1 - t, 5)
                });
            } else {
                // Fallback to native smooth scroll for mobile or when Lenis is not available
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            }
        }
        // If not on homepage, let TransitionLink handle the navigation naturally
    };

    // Updated LogoComponent with conditional behavior
    const LogoComponent = () => {
        if (isHomePage) {
            // On homepage: clickable element that scrolls to top
            return (
                <div
                    onClick={handleLogoClick}
                    className={`${textColorClass} text-xl md:text-2xl font-black tracking-tight z-50 relative hover:opacity-80 transition-opacity cursor-pointer`}
                >
                    Paolo <span className={logoAccentColor}>A.</span>
                </div>
            );
        } else {
            // On other pages: TransitionLink to home
            return (
                <TransitionLink
                    to="/"
                    className={`header-logo-link ${textColorClass} text-xl md:text-2xl font-black tracking-tight z-50 relative hover:opacity-80 transition-opacity cursor-pointer`}
                >
                    Paolo <span className={logoAccentColor}>A.</span>
                </TransitionLink>
            );
        }
    };

    const [menuOpen, setMenuOpen] = useState(false);

    const allLinks = [
        { to: '/', label: t('home') },
        { to: '/works', label: t('works') },
        { to: '/code', label: t('code') },
        { to: '/about', label: t('about') },
        { to: '/articles', label: t('blog') },
        { to: '/playground', label: t('playground') },
    ];

    // Close menu on route change
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    // Lock body scroll when menu is open
    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
            if (window.lenis) window.lenis.stop();
        } else {
            document.body.style.overflow = '';
            if (window.lenis) window.lenis.start();
        }
        return () => {
            document.body.style.overflow = '';
            if (window.lenis) window.lenis.start();
        };
    }, [menuOpen]);

    const hamburgerColor = menuOpen ? 'white' : (isDarkPage ? 'white' : (isScrolled ? 'black' : 'white'));

    const MobileNav = () => (
        <div className="flex items-center gap-1">
            <LanguageSwitcher textColorClass={textColorClass} />
            {/* Hamburger button */}
            <button
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Toggle menu"
                className="relative z-[60] w-9 h-9"
            >
                {/* Top line → rotates to \ */}
                <span style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: '20px',
                    height: '1.5px',
                    background: hamburgerColor,
                    borderRadius: '1px',
                    transformOrigin: 'center',
                    transition: 'transform 0.45s cubic-bezier(0.76,0,0.24,1)',
                    transform: menuOpen
                        ? 'translate(-50%, -50%) rotate(45deg)'
                        : 'translate(-50%, calc(-50% - 6px))',
                }} />
                {/* Middle line → fades out */}
                <span style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: '20px',
                    height: '1.5px',
                    background: hamburgerColor,
                    borderRadius: '1px',
                    transformOrigin: 'center',
                    transition: 'opacity 0.2s ease 0.05s, transform 0.45s cubic-bezier(0.76,0,0.24,1)',
                    transform: 'translate(-50%, -50%)',
                    opacity: menuOpen ? 0 : 1,
                }} />
                {/* Bottom line → rotates to / */}
                <span style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: '20px',
                    height: '1.5px',
                    background: hamburgerColor,
                    borderRadius: '1px',
                    transformOrigin: 'center',
                    transition: 'transform 0.45s cubic-bezier(0.76,0,0.24,1)',
                    transform: menuOpen
                        ? 'translate(-50%, -50%) rotate(-45deg)'
                        : 'translate(-50%, calc(-50% + 6px))',
                }} />
            </button>

            {/* Fullscreen overlay menu */}
            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-50 bg-black flex flex-col justify-between px-8 py-24"
                    >
                        <nav className="flex flex-col gap-2">
                            {allLinks.map((link, i) => (
                                <motion.div
                                    key={link.to}
                                    initial={{ opacity: 0, x: -30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ delay: i * 0.06, duration: 0.3 }}
                                >
                                    <TransitionLink
                                        to={link.to}
                                        onClick={() => setMenuOpen(false)}
                                        className={`block text-5xl font-black tracking-tight text-white hover:text-accent transition-colors py-2 ${location.pathname === link.to ? 'text-accent' : ''}`}
                                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                    >
                                        {link.label}
                                    </TransitionLink>
                                </motion.div>
                            ))}
                        </nav>

                        {/* Bottom: language + footer note */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="flex items-center justify-between"
                        >
                            <LanguageSwitcher textColorClass="text-white" />
                            <span className="text-white/30 text-xs font-mono tracking-widest uppercase">Paolo Allegretti</span>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    const DesktopNav = () => (
        <motion.nav className={`hidden md:flex items-center gap-6 ${isHeroVisible && !isAboutPage && !isWorksPage && !isWorkDetailPage && !isCodePage && !isCodeDetailPage && !isPlaygroundPage && !isArticlesPage ? 'ml-auto' : ''}`}>
            <ul className="flex space-x-8">
                {isCodePage || isCodeDetailPage ? (
                    <>
                        <li>
                            <TransitionLink
                                to="/about"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('about')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>

                            <TransitionLink
                                to="/works"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('works')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>

                        <li>
                            <TransitionLink
                                to="/playground"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('playground')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                    </>
                ) : isAboutPage ? (
                    <>
                        <li>
                            <TransitionLink
                                to="/works"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('works')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/code"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('code')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/articles"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('blog')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/playground"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('playground')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                    </>
                ) : isWorksPage || isWorkDetailPage ? (
                    <>
                        <li>
                            <TransitionLink
                                to="/about"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('about')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/code"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('code')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/articles"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('blog')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/playground"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('playground')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                    </>
                ) : isPlaygroundPage ? (
                    <>
                        <li>
                            <TransitionLink
                                to="/about"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('about')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/works"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('works')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/code"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('code')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/articles"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('blog')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                    </>
                ) : isArticlesPage ? (
                    <>
                        <li>
                            <TransitionLink
                                to="/about"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('about')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/works"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('works')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/code"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('code')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/playground"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('playground')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                    </>
                ) : (
                    <>
                        <li>
                            <TransitionLink
                                to="/about"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('about')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/works"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('works')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>

                        <li>
                            <TransitionLink
                                to="/code"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('code')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/articles"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('blog')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>
                        <li>
                            <TransitionLink
                                to="/playground"
                                className={`header-nav-link transition-colors relative group ${textColorClass} hover:text-accent font-medium`}
                            >
                                {t('playground')}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                            </TransitionLink>
                        </li>

                    </>
                )}
            </ul>
            <LanguageSwitcher textColorClass={textColorClass} />
        </motion.nav>
    );

    return (
        <header
            className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 py-4 md:py-6 ${(isAboutPage && !isAboutDark) ? 'bg-white' : ''
                }`}
        >
            <div className="container mx-auto px-4 md:px-6 flex items-baseline">
                <div className="flex-1">
                    <AnimatePresence>
                        {(!isHeroVisible || isAboutPage || isWorksPage || isWorkDetailPage || isCodePage || isCodeDetailPage || isArticlesPage) && <LogoComponent />}
                    </AnimatePresence>
                </div>

                <div className="flex-shrink-0">
                    {isMobile ? (
                        <MobileNav />
                    ) : (
                        <DesktopNav />
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;